import { IBubble1 } from '../repositories/chats/entities/bubble/Bubble1';
import { IBubbleTextField } from '../repositories/chats/entities/bubble/BubbleTextField';
import { IBubbleTitle } from '../repositories/chats/entities/bubble/BubbleTitle';
import { IRichBubbleV3 } from '../repositories/chats/entities/bubble_v3/RichBubbleV3';
import { ISimpleBubbleV3 } from '../repositories/chats/entities/bubble_v3/SimpleBubbleV3';
import { IMessageListItemV2 } from '../repositories/chats/entities/message_list_item_v2/MessageListItemV2';
import type { TMessageListItemV3_Bubbles_1_Items } from '../repositories/chats/entities/message_list_item_v3/MessageListItemV3';
import { IMessageListItemV31 } from '../repositories/chats/entities/message_list_item_v3/MessageListItemV31';
import { IMessageBubble, TMessageBubbleTextField } from '../types/messageBubble';
import { TMessageSchema } from '../types/messages';

type TSourceMessage = IMessageListItemV2 | IMessageListItemV31;
type TSourceBubble = IBubble1 | ISimpleBubbleV3 | IRichBubbleV3;

const ACCEPTABLE_BUBBLE_TYPES: TMessageListItemV3_Bubbles_1_Items['type'][] = ['simple', 'rich'];

const mapBubbleTitle = (title?: IBubbleTitle | null): IBubbleTitle => title ?? { label: '' };

const mapBubbleTextField = (bubbleTextField: IBubbleTextField): TMessageBubbleTextField | null => {
  const { buttonName, socketPayload, textFieldType, hint } = bubbleTextField;

  return {
    buttonName,
    socketPayload,
    textFieldType: textFieldType ?? undefined,
    hint: hint ?? undefined,
  };
};

const mapLegacyBubble = (bubble: IBubble1): IMessageBubble => {
  const { title, analyticsBubbleType, textField, buttons } = bubble;

  return {
    title: mapBubbleTitle(title),
    analyticsBubbleType,
    body: null,
    textField: textField ? mapBubbleTextField(textField) : null,
    buttons: buttons ?? null,
  };
};

const mapSimpleBubble = (bubble: ISimpleBubbleV3): IMessageBubble => {
  const { content, analyticsBubbleType } = bubble;
  const { title, textField, buttons } = content;

  return {
    title: mapBubbleTitle(title),
    analyticsBubbleType,
    body: null,
    textField: textField ? mapBubbleTextField(textField) : null,
    buttons: buttons ?? null,
  };
};

const mapRichBubble = (bubble: IRichBubbleV3): IMessageBubble => {
  const { content, analyticsBubbleType } = bubble;
  const { title, body, button } = content;

  return {
    title: { label: title ?? '' },
    analyticsBubbleType,
    body,
    textField: null,
    buttons: button ? [button] : null,
  };
};

/**
 * Приводит все входящие форматы бабблов к единой строгой модели (title/body/textField/buttons):
 * - старый плоский формат (IBubble1) — раскладываем поля как есть;
 * - simple (SimpleBubbleV3) — раскладываем content в title/textField/buttons;
 * - rich (RichBubbleV3) — title/body/button кладём в title/body/buttons.
 */
const mapBubble = (bubble: TSourceBubble): IMessageBubble | null => {
  if (!('content' in bubble)) {
    return mapLegacyBubble(bubble);
  }

  if (!ACCEPTABLE_BUBBLE_TYPES.includes(bubble.type)) {
    return null;
  }

  if (bubble.type === 'rich') {
    return mapRichBubble(bubble);
  }

  return mapSimpleBubble(bubble);
};

const mapBubbles = (bubbles: TSourceMessage['bubbles']): IMessageBubble[] | null => {
  if (!bubbles) {
    return null;
  }

  return bubbles.map(mapBubble).filter(Boolean) as IMessageBubble[];
};

export const mapMessage = (message: TSourceMessage): TMessageSchema => {
  return {
    ...message,
    bubbles: mapBubbles(message.bubbles),
    unread: message.status !== 'readByRecipient' && message.status !== 'deleted',
    type: message.type,
  };
};
